/**
 * 都市商务模板 - 导航栏
 *
 * 设计理念：深藏青 + 活力橙，沉稳的都市商务感
 * - 深藏青(#1B3A5C) 深色导航栏，白色文字
 * - 活力橙(#F08A24) 发布按钮与激活态高亮
 * - 直角圆角混搭（rounded-lg），利落不拖沓
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function UrbanNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const { siteId, loading: siteLoading, siteName: contextSiteName } = useSite();
  const { siteConfig, navMenus, navCategories } = useSiteConfig();
  const enableVip = siteConfig?.enable_vip ?? true;

  const siteName = contextSiteName;

  useEffect(() => {
    if (!siteConfig) return;
    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate("/");
  };

  const isHome = location.pathname === "/";

  return (
    <>
      {/* 顶部品牌色条 */}
      <div className="h-1 bg-gradient-to-r from-[#1B3A5C] via-[#2C5A8C] to-[#F08A24]" />

      <nav className="sticky top-0 z-50 bg-[#1B3A5C] shadow-[0_4px_20px_-6px_rgba(27,58,92,0.35)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[4.2rem]">

            {/* ── 左侧：Logo + 站点名 ── */}
            <Link to="/" className="flex items-center gap-3 shrink-0 group">
              {logo && !logoError ? (
                <img
                  src={logo}
                  alt={siteName}
                  className="h-10 w-auto object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#F08A24] to-[#E26A1B] flex items-center justify-center text-white font-black text-lg shadow-lg shadow-orange-500/30">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
              {siteName && (
                <span className="text-lg font-bold text-white tracking-tight group-hover:text-[#F5A94D] transition-colors">
                  {siteName}
                </span>
              )}
            </Link>

            {/* ── 中间：主导航（桌面端）── */}
            <div className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2.5 text-[15px] font-medium rounded-lg transition-all ${
                  isHome
                    ? "text-[#F08A24] bg-white/10"
                    : "text-gray-200 hover:text-[#F5A94D] hover:bg-white/10"
                }`}
              >
                首页
              </Link>
              {navMenus.map((menu) => (
                <Link
                  key={menu.id}
                  to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                       menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                       menu.type === 'link' ? (menu.target_url || '#') : '#'}
                  className="px-4 py-2.5 text-[15px] font-medium rounded-lg text-gray-200 hover:text-[#F5A94D] hover:bg-white/10 transition-all"
                >
                  {menu.name}
                </Link>
              ))}
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2.5 text-[15px] font-medium rounded-lg transition-all ${
                    location.pathname === `/category/${cat.slug}`
                      ? "text-[#F08A24] bg-white/10"
                      : "text-gray-200 hover:text-[#F5A94D] hover:bg-white/10"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* ── 右侧：用户区 + 发布按钮 ── */}
            <div className="flex items-center gap-3">
              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F08A24] to-[#E26A1B] flex items-center justify-center text-white text-xs font-bold overflow-hidden shadow-sm">
                      {user?.avatar ? (
                        <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (user?.nickname || 'U').charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="text-sm text-gray-200 hidden md:inline max-w-[80px] truncate">
                      {user?.nickname}
                    </span>
                    <i className="ri-arrow-down-s-line text-gray-300 text-xs" />
                  </button>

                  {isUserMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsUserMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-20">
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-orange-50 hover:text-[#E26A1B] transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-user-line text-base" />会员中心
                        </Link>
                        <Link
                          to="/dashboard/my-listings"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-orange-50 hover:text-[#E26A1B] transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-file-list-line text-base" />我的发布
                        </Link>
                        {enableVip && (
                          <Link
                            to="/vip"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#E26A1B] font-medium hover:bg-orange-50 transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <i className="ri-vip-crown-line text-base" />开通VIP
                          </Link>
                        )}
                        {isAdmin && (
                          <>
                            <div className="border-t border-gray-100 my-1" />
                            <Link
                              to="/admin"
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-orange-50 hover:text-[#E26A1B] transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <i className="ri-shield-star-line text-base" />后台管理
                            </Link>
                          </>
                        )}
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50 hover:text-red-500 transition-colors w-full text-left"
                        >
                          <i className="ri-logout-box-line text-base" />退出登录
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => navigate("/login")}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-200 rounded-lg hover:bg-white/10 hover:text-white transition-colors"
                >
                  <i className="ri-user-line" />
                  <span>登录</span>
                </button>
              )}

              <button
                onClick={() => navigate("/publish")}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-bold bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white rounded-lg hover:from-[#E26A1B] hover:to-[#C95A12] transition-all shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30"
              >
                <i className="ri-add-line text-base" />
                <span className="hidden sm:inline">发布信息</span>
              </button>

              {/* 移动端菜单按钮 */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-xl text-white`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── 移动端菜单 ── */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-[#1B3A5C] shadow-lg">
            <div className="px-4 py-3 space-y-1 max-h-[60vh] overflow-y-auto">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 text-[15px] font-medium rounded-lg transition-colors ${
                  isHome ? "text-[#F08A24] bg-white/10" : "text-gray-100 hover:bg-white/10"
                }`}
              >
                <i className="ri-home-4-line text-lg" />首页
              </Link>
              {navMenus.map((menu) => (
                <Link
                  key={menu.id}
                  to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                       menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                       menu.type === 'link' ? (menu.target_url || '#') : '#'}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-100 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <i className="ri-menu-line text-lg" />{menu.name}
                </Link>
              ))}
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-[15px] font-medium rounded-lg transition-colors ${
                    location.pathname === `/category/${cat.slug}` ? "text-[#F08A24] bg-white/10" : "text-gray-100 hover:bg-white/10"
                  }`}
                >
                  <i className="ri-grid-line text-lg" />{cat.name}
                </Link>
              ))}

              {enableVip && (
                <Link
                  to="/vip"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-[#F5A94D] rounded-lg hover:bg-white/10 transition-colors"
                >
                  <i className="ri-vip-crown-line text-lg" />开通VIP
                </Link>
              )}

              <div className="border-t border-white/10 my-2" />

              {isLoggedIn ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-100 rounded-lg hover:bg-white/10 transition-colors">
                    <i className="ri-user-line text-lg" />会员中心
                  </Link>
                  <Link to="/dashboard/my-listings" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-100 rounded-lg hover:bg-white/10 transition-colors">
                    <i className="ri-file-list-line text-lg" />我的发布
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-100 rounded-lg hover:bg-white/10 transition-colors">
                      <i className="ri-shield-star-line text-lg" />后台管理
                    </Link>
                  )}
                  <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-300 rounded-lg hover:bg-white/10 transition-colors w-full">
                    <i className="ri-logout-box-line text-lg" />退出登录
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-[#F08A24] rounded-lg hover:bg-white/10 transition-colors">
                  <i className="ri-user-line text-lg" />登录 / 注册
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
